import { logger } from "@/lib/logger";
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteContext } from '@/contexts/SiteContext';
import { useSMOCROAccess } from '@/hooks/useSMOCROAccess';
import { useToast } from '@/hooks/use-toast';

interface InAppNotification {
  id: string;
  title: string;
  content: string;
  priority: number;
  created_at: string;
  read: boolean;
  type: string;
  metadata?: Record<string, any>;
  trial_site_id?: string | null;
  organization_id?: string | null;
}

export type NotificationScope = 'admin' | 'organization' | 'trial_site' | 'personal';

interface SiteAwareNotificationContext {
  scope: NotificationScope;
  scopeId: string | null;
  scopeName: string | null;
  effectiveSiteId: string | null;
  organizationId: string | null;
  isAdmin: boolean;
}

interface UseSiteAwareNotificationsResult {
  notifications: InAppNotification[];
  context: SiteAwareNotificationContext;
  unreadCount: number;
  loading: boolean;
  error: Error | null;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => void;
}

interface UseSiteAwareNotificationsOptions {
  /** Override the site ID for filtering - used when admin is viewing a specific site's dashboard */
  overrideSiteId?: string | null;
  /** Override the site name for display */
  overrideSiteName?: string | null;
  /** Whether to enable the query (for lazy-loading). Defaults to true. */
  enabled?: boolean;
}

/**
 * Site-aware notifications hook
 * 
 * Filtering logic:
 * - If overrideSiteId is provided: Filter to that specific site (page context takes priority)
 * - ADMIN (no override): See ALL platform notifications (no filtering)
 * - CRO/SMO Users: See notifications for their organization
 * - Trial Site Staff: See notifications for their specific site
 * - Personal: Only personal notifications (fallback)
 */
export function useSiteAwareNotifications(options?: UseSiteAwareNotificationsOptions): UseSiteAwareNotificationsResult {
  const { overrideSiteId, overrideSiteName, enabled: externalEnabled = true } = options || {};
  const { user, userProfile, isAdmin } = useAuth();
  const { site } = useSiteContext();
  const { isSMOCROUser, organizationId, organizationName } = useSMOCROAccess();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Determine the effective context for notification filtering
  const notificationContext = useMemo((): SiteAwareNotificationContext => {
    // Priority 0: Override from page context (admin viewing specific site)
    if (overrideSiteId) {
      logger.debug(`[SiteAwareNotifications] Using override site ID: ${overrideSiteId}`);
      return {
        scope: 'trial_site',
        scopeId: overrideSiteId,
        scopeName: overrideSiteName || 'Site Dashboard',
        effectiveSiteId: overrideSiteId,
        organizationId: null,
        isAdmin: isAdmin, // Still track if user is admin
      };
    }

    // Priority 1: If on a subdomain site, scope to that site (even for admins)
    if (site?.id) {
      logger.debug(`[SiteAwareNotifications] Subdomain site context detected: ${site.id}`);
      return {
        scope: 'trial_site',
        scopeId: site.id,
        scopeName: site.display_name || 'Trial Site',
        effectiveSiteId: site.id,
        organizationId: null,
        isAdmin: isAdmin,
      };
    }

    // Priority 2: Admin users on main domain see everything
    if (isAdmin) {
      return {
        scope: 'admin',
        scopeId: null,
        scopeName: 'Platform-wide',
        effectiveSiteId: null,
        organizationId: null,
        isAdmin: true,
      };
    }

    // Priority 3: CRO/SMO users see their organization's notifications
    if (isSMOCROUser && organizationId) {
      return {
        scope: 'organization',
        scopeId: organizationId,
        scopeName: organizationName || 'Organization',
        effectiveSiteId: null,
        organizationId: organizationId,
        isAdmin: false,
      };
    }

    // Priority 4: Site context from user profile
    if (userProfile?.trial_site_id) {
      return {
        scope: 'trial_site',
        scopeId: userProfile.trial_site_id,
        scopeName: 'Your Site',
        effectiveSiteId: userProfile.trial_site_id,
        organizationId: null,
        isAdmin: false,
      };
    }

    // Fallback: Personal notifications only
    return {
      scope: 'personal',
      scopeId: user?.id || null,
      scopeName: 'Personal',
      effectiveSiteId: null,
      organizationId: null,
      isAdmin: false,
    };
  }, [isAdmin, isSMOCROUser, organizationId, organizationName, site, userProfile?.trial_site_id, user?.id, overrideSiteId, overrideSiteName]);

  // Build the scoped query
  const fetchNotifications = useCallback(async (): Promise<InAppNotification[]> => {
    if (!user?.id) return [];

    let query = supabase
      .from('notification_logs')
      .select('*')
      .eq('notification_type', 'in_app')
      .order('created_at', { ascending: false })
      .limit(50);

    // Apply role-based filtering
    switch (notificationContext.scope) {
      case 'admin':
        // Admin sees all - no additional filters
        break;
      
      case 'organization':
        // CRO/SMO sees organization-wide notifications
        if (notificationContext.organizationId) {
          query = query.eq('organization_id', notificationContext.organizationId);
        }
        break;
      
      case 'trial_site':
        // Trial site staff sees site-specific notifications
        if (notificationContext.effectiveSiteId) {
          query = query.eq('trial_site_id', notificationContext.effectiveSiteId);
        }
        break;
      
      case 'personal':
      default:
        // Fallback to personal only
        query = query.eq('user_id', user.id);
        break;
    }

    const { data, error } = await query;

    if (error) {
      logger.error('[SiteAwareNotifications] Fetch error:', error);
      throw error;
    }

    // Transform to InAppNotification format
    return (data || []).map(log => {
      const metadata = (log.metadata as Record<string, any>) || {};
      return {
        id: log.id,
        title: metadata.title || 'Notification',
        content: metadata.content || '',
        priority: metadata.priority || 3,
        created_at: metadata.created_at || log.created_at,
        read: metadata.read || false,
        type: metadata.notification_type || 'general',
        metadata: metadata,
        trial_site_id: log.trial_site_id,
        organization_id: log.organization_id,
      };
    });
  }, [user?.id, notificationContext]);

  // Use React Query for data fetching with caching
  const {
    data: notifications = [],
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['site-aware-notifications', user?.id, notificationContext.scope, notificationContext.scopeId],
    queryFn: fetchNotifications,
    enabled: !!user?.id && externalEnabled,
    staleTime: 60 * 1000, // 1 minute (was 30s — reduce pressure)
    refetchOnWindowFocus: false, // Hardened: don't refetch on focus
  });

  // Set up real-time subscription based on scope
  useEffect(() => {
    if (!user?.id) return;

    // Build channel name based on scope
    const channelName = notificationContext.scope === 'admin'
      ? 'notifications:admin'
      : notificationContext.scope === 'organization' && notificationContext.organizationId
        ? `notifications:org:${notificationContext.organizationId}`
        : notificationContext.scope === 'trial_site' && notificationContext.effectiveSiteId
          ? `notifications:site:${notificationContext.effectiveSiteId}`
          : `notifications:user:${user.id}`;

    logger.debug(`[SiteAwareNotifications] Setting up real-time channel: ${channelName}`);

    // Broadcast channel for real-time notifications
    const broadcastChannel = supabase
      .channel(channelName)
      .on('broadcast', { event: 'new_notification' }, (payload) => {
        logger.debug('[SiteAwareNotifications] New notification received:', payload);
        
        // Invalidate query to refetch
        queryClient.invalidateQueries({ 
          queryKey: ['site-aware-notifications', user.id] 
        });

        // Show toast for high priority
        if (payload.payload?.priority <= 2) {
          toast({
            title: payload.payload.title || 'New Notification',
            description: (payload.payload.content || '').substring(0, 100),
            duration: 5000,
          });
        }
      })
      .subscribe();

    // Postgres changes subscription for insert events
    const dbChannel = supabase
      .channel(`notification_logs_changes:${notificationContext.scopeId || user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification_logs',
          filter: notificationContext.scope === 'organization' && notificationContext.organizationId
            ? `organization_id=eq.${notificationContext.organizationId}`
            : notificationContext.scope === 'trial_site' && notificationContext.effectiveSiteId
              ? `trial_site_id=eq.${notificationContext.effectiveSiteId}`
              : `user_id=eq.${user.id}`,
        },
        (payload) => {
          logger.debug('[SiteAwareNotifications] DB change detected:', payload);
          queryClient.invalidateQueries({ 
            queryKey: ['site-aware-notifications', user.id] 
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(broadcastChannel);
      supabase.removeChannel(dbChannel);
    };
  }, [user?.id, notificationContext, queryClient, toast]);

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user?.id) return;

    try {
      // Track engagement via edge function
      await supabase.functions.invoke('intelligent-notification-processor', {
        body: {
          trackEngagement: true,
          userId: user.id,
          notificationId,
          engagementAction: 'opened'
        }
      });

      // Optimistically update local cache
      queryClient.setQueryData<InAppNotification[]>(
        ['site-aware-notifications', user.id, notificationContext.scope, notificationContext.scopeId],
        (old) => old?.map(n => n.id === notificationId ? { ...n, read: true } : n) || []
      );
    } catch (error) {
      logger.error('[SiteAwareNotifications] Error marking as read:', error);
    }
  }, [user?.id, queryClient, notificationContext]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;

    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      
      for (const notification of unreadNotifications) {
        await markAsRead(notification.id);
      }

      toast({
        title: "Success",
        description: "All notifications marked as read",
      });
    } catch (error) {
      logger.error('[SiteAwareNotifications] Error marking all as read:', error);
    }
  }, [user?.id, notifications, markAsRead, toast]);

  // Calculate unread count
  const unreadCount = useMemo(() => 
    notifications.filter(n => !n.read).length,
    [notifications]
  );

  return {
    notifications,
    context: notificationContext,
    unreadCount,
    loading,
    error: error as Error | null,
    markAsRead,
    markAllAsRead,
    refresh: refetch,
  };
}
